import { PixelCrop } from 'react-image-crop';
import { ProcessedImageResult } from './types';

// Helper function to create a canvas element
export function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });
}

// Get cropped image with quality
export async function getCroppedImgWithQuality(
  imageSrc: string,
  pixelCrop: PixelCrop,
  quality: number,
  displayedWidth: number,
  displayedHeight: number,
  format: string,
  outputWidth?: number,
  outputHeight?: number
): Promise<ProcessedImageResult> {
  const image = await createImage(imageSrc);
  // Calculate the ratio between display size and natural size
  const ratioX = image.naturalWidth / displayedWidth;
  const ratioY = image.naturalHeight / displayedHeight;

  // Calculate the actual crop dimensions in natural image coordinates
  const cropX = pixelCrop.x * ratioX;
  const cropY = pixelCrop.y * ratioY;
  const cropWidth = pixelCrop.width * ratioX;
  const cropHeight = pixelCrop.height * ratioY;

  // Set initial canvas size to the crop size
  let canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');
  canvas.width = cropWidth;
  canvas.height = cropHeight;

  // Draw the cropped portion of the image
  ctx.drawImage(
    image,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  // If resizing is requested, create a new canvas and draw the cropped image scaled
  if (outputWidth && outputHeight) {
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = outputWidth;
    resizedCanvas.height = outputHeight;
    const resizedCtx = resizedCanvas.getContext('2d');
    if (!resizedCtx) throw new Error('No 2d context');
    resizedCtx.drawImage(canvas, 0, 0, outputWidth, outputHeight);
    canvas = resizedCanvas;
  }

  // Determine mime type
  let mime = 'image/jpeg';
  if (format === 'png') mime = 'image/png';
  else if (format === 'webp') mime = 'image/webp';
  else if (format === 'bmp') mime = 'image/bmp';
  else if (format === 'avif') mime = 'image/avif';
  else if (format === 'jpeg') mime = 'image/jpeg';
  else if (format === 'jpg') mime = 'image/jpeg';

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) throw new Error('Canvas is empty');
      resolve({ url: URL.createObjectURL(blob), size: blob.size });
    }, mime, quality);
  });
}

// Compress full image (no crop)
export async function compressFullImage(
  imageSrc: string,
  quality: number
): Promise<ProcessedImageResult> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');
  canvas.width = image.width;
  canvas.height = image.height;
  ctx.drawImage(image, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) throw new Error('Canvas is empty');
      resolve({ url: URL.createObjectURL(blob), size: blob.size });
    }, 'image/jpeg', quality);
  });
}

// Download processed image
export function downloadProcessedImage(url: string, filename: string = 'processed-image.jpg'): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
} 